/** Hedef Teslim Tarihi (Termin) Güncelleme Talebi tip tanımları (087). Backend DTO'larıyla birebir. */

/** Sayfalı liste yanıtı (anayasa API Standards — PagedModel biçimi). */
export interface PagedResponse<T> {
  content: T[]
  page: {
    size: number
    number: number
    totalElements: number
    totalPages: number
  }
}

/**
 * Talep durumu kimlikleri — `REFERANS(TYPE='DEADLINE_UPDATE_REQUEST_STATUS')`. Değerler DB'de check
 * constraint ile kilitlidir; **aksiyon statüsünden bağımsızdır**.
 *
 * ⚠ Bu sabitlerle yalnız **kimlik** taşınır — **rozet rengi** eşlemesi (`requestStatusTone`) ve "Onay
 * Bekleyenler" sekmesinin varsayılan durum filtresi (`PENDING`). Durum **adları** koda gömülmez; hem
 * tablo etiketi hem filtre combosu adları sunucudan `REFERANS`'tan, `Accept-Language`'e göre çözülmüş
 * olarak gelir (`getDeadlineRequestStatuses`).
 */
export const DEADLINE_REQUEST_STATUS = {
  PENDING: 400,
  APPROVED: 401,
  REJECTED: 402,
  /**
   * 403 — **İptal Edildi** (katalogdaki ad; ekranda gösterilen metin sunucudan gelir, buraya
   * gömülmez). Yalnız **sistem** yazar (kullanıcı iptal edemez, ekranda böyle bir eylem yoktur):
   * talebe konu görev, talep girilemeyecek bir statüye geçtiğinde (ör. Tamamlandı) bekleyen talep
   * otomatik düşer — iş bittiği için isteğin karşılığı kalmaz (kullanıcı kuralı 2026-08-28).
   */
  CANCELLED: 403,
} as const

/**
 * Ekranın iki sekmesi (kullanıcı kararı 2026-08-14) — backend `DeadlineRequestTab` ile **birebir**
 * aynı adlar.
 *
 * Sekme sunucuda bir kapsam **daraltmasıdır**: `MINE` = yalnız kendi girdiğim talepler, `INBOX` =
 * yalnız bana düşen onaylar. İkisi de eski tek-liste kapsamının alt kümesi olduğu için sekmeyi
 * istemcinin göndermesi görünürlüğü genişletmez.
 */
export const DEADLINE_REQUEST_TAB = {
  MINE: 'MINE',
  INBOX: 'INBOX',
} as const

export type DeadlineRequestTab = (typeof DEADLINE_REQUEST_TAB)[keyof typeof DEADLINE_REQUEST_TAB]

/** Filtre combosu seçeneği — `REFERANS`'tan gelir (backend `LookupItemDto`). */
export interface DeadlineRequestStatusOption {
  id: number
  name: string
}

/** Liste satırı / yazma ucu yanıtı. */
export interface DeadlineRequest {
  id: number
  actionId: number
  /** Görünen madde numarası (ör. `A-7`); numarasız taslak maddede null. */
  actionRecordNumber: string | null
  actionName: string | null
  /** Alt aksiyon mu? (onay mercii her hâlükârda **kök** aksiyonun oluşturanıdır.) */
  subAction: boolean
  actionStatusId: number | null
  actionStatusName: string | null
  /** Aksiyonun **o anki** termini. Onaydan sonra onaylanan tarihe eşitlenir. */
  currentDeadline: string | null
  /**
   * Üst aksiyonun termini — yalnız **alt** aksiyonda dolu. Onay pop-up'ı bunu takvimin `maxDate`'i
   * yapar: alt aksiyonun termini üst aksiyonunkini aşamaz (066 değişmezi). Alan olmadan onay mercii
   * geçersiz bir tarih seçebiliyor ve ancak sunucudan hata alıyordu.
   */
  parentDeadline: string | null
  /** Talep edilen yeni termin — onayda **üzerine yazılmaz**, orijinal korunur. */
  requestedDate: string
  requestNote: string
  statusId: number
  statusName: string | null
  /** Ret açıklaması — yalnız reddedilen taleplerde dolu. */
  rejectNote: string | null
  requestUserId: number | null
  requestUserName: string | null
  /** **Onay mercii**: kök aksiyonu oluşturan kullanıcı (talep ona düşer). */
  approverUserId: number | null
  approverUserName: string | null
  approveOrRejectTime: string | null
  /** Kararı **veren** kullanıcı; ayrıcalıklı yetkili işlediyse onay merciinden farklı olabilir. */
  approveOrRejectUserId: number | null
  approveOrRejectUserName: string | null
  creDate: string
  /**
   * Bu satırda Onayla/Reddet gösterilsin mi. **Sunucu hesaplar** (talep Beklemede **ve** çağıran kök
   * aksiyonun oluşturanı ya da ayrıcalıklı yetkili); istemci bu kuralı yeniden hesaplamaz.
   */
  canDecide: boolean
  /** Optimistic lock — onay/ret isteğinde geri gönderilir. */
  modDate: string
}

/** Yeni talep girdisi. Talep sahibi ve durum sunucuda atanır. */
export interface DeadlineRequestCreateInput {
  actionId: number
  /** `yyyy-MM-dd`; bugünden önce olamaz. Mevcut terminden önce ya da sonra olması **serbesttir**. */
  requestedDate: string
  requestNote: string
}

/** Onay girdisi. `approvedDeadline` talep edilen tarihle dolu açılır, değiştirilebilir. */
export interface DeadlineRequestApproveInput {
  approvedDeadline: string
  modDate: string
}

/** Ret girdisi. Açıklama **koşulsuz zorunludur**. */
export interface DeadlineRequestRejectInput {
  rejectNote: string
  modDate: string
}
